import React from 'react';
import styles from './aims.module.css';
import { AGE_MAX_MONTHS, TOTAL_MAX } from '../../modules/aims/aims';
import { toMonths } from '../../modules/motorMilestones/motorMilestones';

/**
 * Evolução do total da AIMS pela idade (corrigida no prematuro), em SVG puro
 * (só traços e texto) para funcionar igual na tela e no PDF (html2canvas).
 * Desenha só as avaliações do próprio paciente: as curvas de percentil da
 * escala são licenciadas e não são reproduzidas.
 *
 * `rows`: linhas de buildRows; `highlightId`: avaliação em destaque.
 * Sem pelo menos duas avaliações com idade e total, não desenha nada.
 */

const WIDTH = 640;
const HEIGHT = 240;
const MARGIN = { top: 18, right: 18, bottom: 38, left: 46 };
const Y_TICKS = [0, 10, 20, 30, 40, 50, TOTAL_MAX];

const TONE_COLORS = { alert: '#DD6B20', info: '#3182CE', ok: '#38A169' };
const NO_BAND_COLOR = '#A0AEC0';
const PATH_COLOR = '#319795';
const GRID_COLOR = '#EDF2F7';
const TEXT_COLOR = '#718096';

const LEGEND = [
  { tone: 'ok', label: 'Desenvolvimento esperado' },
  { tone: 'info', label: 'Suspeito: acompanhar' },
  { tone: 'alert', label: 'Atraso motor' },
  { tone: null, label: 'Sem percentil' },
];

const colorOf = (band) => (band ? TONE_COLORS[band.tone] : NO_BAND_COLOR);

export const chartPoints = (rows) => (Array.isArray(rows) ? rows : [])
  .filter((row) => row.age && row.total != null)
  .map((row) => ({ id: row.record.id, x: toMonths(row.age.days), y: row.total, band: row.band }))
  .sort((a, b) => a.x - b.x);

const AimsChart = ({ rows, highlightId }) => {
  const points = chartPoints(rows);
  if (points.length < 2) return null;

  const to = Math.max(AGE_MAX_MONTHS, Math.ceil(points[points.length - 1].x / 3) * 3);
  const plotWidth = WIDTH - MARGIN.left - MARGIN.right;
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const sx = (x) => MARGIN.left + (x / to) * plotWidth;
  const sy = (y) => MARGIN.top + (1 - y / TOTAL_MAX) * plotHeight;
  const path = points.map((point, index) => `${index ? 'L' : 'M'}${sx(point.x).toFixed(1)} ${sy(point.y).toFixed(1)}`).join(' ');
  const xTicks = Array.from({ length: Math.floor(to / 3) + 1 }, (_, index) => index * 3);
  const tones = new Set(points.map((point) => (point.band ? point.band.tone : null)));

  return (
    <div className={styles.chart}>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%" role="img" aria-label="Evolução do total da escala de Alberta" style={{ display: 'block' }}>
        {Y_TICKS.map((tick) => (
          <g key={`y${tick}`}>
            <line x1={MARGIN.left} x2={WIDTH - MARGIN.right} y1={sy(tick)} y2={sy(tick)} stroke={GRID_COLOR} strokeWidth="1" />
            <text x={MARGIN.left - 6} y={sy(tick) + 3.5} textAnchor="end" fontSize="10" fill={TEXT_COLOR}>{tick}</text>
          </g>
        ))}
        {xTicks.map((tick) => (
          <g key={`x${tick}`}>
            <line x1={sx(tick)} x2={sx(tick)} y1={MARGIN.top} y2={MARGIN.top + plotHeight} stroke={GRID_COLOR} strokeWidth="1" />
            <text x={sx(tick)} y={MARGIN.top + plotHeight + 14} textAnchor="middle" fontSize="10" fill={TEXT_COLOR}>{tick}</text>
          </g>
        ))}
        {to > AGE_MAX_MONTHS && (
          <line x1={sx(AGE_MAX_MONTHS)} x2={sx(AGE_MAX_MONTHS)} y1={MARGIN.top} y2={MARGIN.top + plotHeight}
            stroke={TEXT_COLOR} strokeWidth="1" strokeDasharray="4 3" />
        )}
        <text x={MARGIN.left + plotWidth / 2} y={HEIGHT - 6} textAnchor="middle" fontSize="10.5" fill={TEXT_COLOR}>Idade (meses)</text>
        <text x={12} y={MARGIN.top + plotHeight / 2} textAnchor="middle" fontSize="10.5" fill={TEXT_COLOR}
          transform={`rotate(-90 12 ${MARGIN.top + plotHeight / 2})`}>
          {`Total (0 a ${TOTAL_MAX})`}
        </text>

        <path d={path} fill="none" stroke={PATH_COLOR} strokeWidth="1.6" />
        {points.map((point) => {
          const featured = String(point.id) === String(highlightId);
          return (
            <g key={point.id}>
              <circle cx={sx(point.x)} cy={sy(point.y)} r={featured ? 5.5 : 4} fill={colorOf(point.band)} stroke="#FFFFFF" strokeWidth="1.5" />
              <text x={sx(point.x)} y={sy(point.y) - 9} textAnchor="middle" fontSize="10" fontWeight={featured ? 700 : 600} fill="#2D3748">
                {point.y}
              </text>
            </g>
          );
        })}
      </svg>
      <div className={styles.legend}>
        {LEGEND.filter((item) => tones.has(item.tone)).map((item) => (
          <span key={item.label} className={styles.legendItem}>
            <span className={styles.legendDot} style={{ background: item.tone ? TONE_COLORS[item.tone] : NO_BAND_COLOR }} />
            {item.label}
          </span>
        ))}
        <span className={styles.legendItem}>Só as avaliações deste paciente, sem as curvas da escala</span>
      </div>
    </div>
  );
};

export default AimsChart;
